'use client'

import { Box, Button, Modal, Stack, Typography } from '@mui/material'
import { Route } from 'next'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Trans } from 'react-i18next'
import { SvgAsset } from '~/components/SvgAsset'
import { URLConstants } from '~/libs/constants'
import browserEnv from '~/libs/env/browserEnv'

const LOCAL_STORAGE_KEY = 'app_open_modal'
const DELAY = 60 * 60 * 1 // 1 hour

export const AppOpenModal = () => {
  const [showModal, setShowModal] = useState(false)
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const appLink = useMemo(() => {
    const currentUrl = `${pathname}${
      searchParams && searchParams.toString() ? `?${searchParams}` : ''
    }`
    return browserEnv.APP_LINK + currentUrl
  }, [pathname, searchParams])

  useEffect(() => {
    const localStorageData = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (localStorageData) {
      const { time } = JSON.parse(localStorageData)
      const currentTime = Math.floor(Date.now() / 1000)

      if (currentTime - time < DELAY) {
        return
      }
    }

    const referrer = document.referrer
    const isExternal = !referrer || referrer === '' || !referrer.includes(window.location.hostname)

    if (isExternal && isMobile) {
      setShowModal(true)
    }
  }, [])

  const handleContinueOnWeb = useCallback(() => {
    const count = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{ "count": 0 }').count

    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({ time: Math.floor(Date.now() / 1000), count: count + 1 }),
    )
    setShowModal(false)
  }, [])

  return (
    <Modal
      open={showModal}
      onClose={handleContinueOnWeb}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <Stack
        spacing={2}
        sx={{
          backgroundColor: '#212121',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '90%',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Box sx={{}}>
          <SvgAsset asset='logo' />
        </Box>
        <Typography variant='h2' fontWeight={700}>
          <Trans i18nKey='Enjoy a smoother experience with the SAMANSA app' />
        </Typography>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <Link href={appLink as Route} passHref style={{ textDecoration: 'none', width: '100%' }}>
            <Button variant='contained' fullWidth onClick={() => setShowModal(false)}>
              <Trans i18nKey='open_app' />
            </Button>
          </Link>
          <Link
            href={URLConstants.appStorePage as Route}
            passHref
            style={{ textDecoration: 'none', width: '100%' }}
          >
            <Button variant='outlined' fullWidth onClick={() => setShowModal(false)}>
              <Trans i18nKey='Download the app' />
            </Button>
          </Link>
          <Button variant='text' fullWidth onClick={handleContinueOnWeb}>
            <Trans i18nKey='Continue on the web' />
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}
